import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router/dom";
import { GraphQLProvider } from "./graphql/provider";
import { IntlProvider } from "./i18n/IntlProvider";
import { makeRouter } from "./router";
import { bootstrapAuth } from "./auth/me";
import { installSessionWatch } from "./auth/session-watch";
import { installRuntimeDiagnostics } from "./lib/diagnostics/runtime";
import { logDiagnostic } from "./lib/diagnostics";
import { CODES } from "./lib/diagnostics/contract";
import "./index.css";

// Bootstrap order:
//   1. /auth/me - populates CSRF token + auth store BEFORE the router
//      mounts, so loaders see consistent state on first render.
//   2. Build router (basename comes from /config.js runtime config).
//   3. Render with Apollo + Router providers.
//
// /auth/me is allowed to fail silently - the auth store flips to
// `anonymous` and the requireAuth loader will redirect to login.

async function bootstrap(): Promise<void> {
  // Install global runtime capture before anything renders. Delivery stays
  // gated on the authenticated + flag-enabled logger, so pre-auth errors are
  // safely dropped.
  installRuntimeDiagnostics();

  try {
    await bootstrapAuth();
  } catch (err) {
    // Pre-auth: the logger drops this until authenticated, but route it through
    // the boundary rather than a bare console call. Keep the dev console hint.
    logDiagnostic({
      level: "warn",
      code: CODES.application.QUERY_FAILURE,
      category: "application",
      details: { action: "auth.bootstrap" },
    });
    if (import.meta.env.DEV) console.warn("Initial /auth/me failed; treating as anonymous", err);
  }

  // After bootstrap: the watcher only acts on an authenticated store, and the
  // first /auth/me is what establishes that.
  installSessionWatch();

  const router = makeRouter();
  const root = document.getElementById("root");
  if (!root) throw new Error("#root not found in index.html");

  createRoot(root).render(
    <StrictMode>
      <IntlProvider>
        <GraphQLProvider>
          <RouterProvider router={router} />
        </GraphQLProvider>
      </IntlProvider>
    </StrictMode>,
  );
}

void bootstrap();
